import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  ArrowRight,
  ArrowDown,
  Play,
  Calendar,
  Users,
  Cpu,
  Music,
  Palette,
  Globe2,
  Heart,
  MapPin,
  X,
} from "lucide-react";
import { Container, FadeIn, SectionHeading, SectionLabel, SectionDivider } from "@/components/Section";
import { PILLARS, IMG, TESTIMONIALS, PRESS, IMPACT_STATS, TOK_ASSETS, TOK_VOICE, TOK_SHOWS } from "@/lib/data";
import { TOK_PHILOSOPHY, TOK_PHILOSOPHY_BY_KEY } from "@/lib/philosophy";
import useContent from "@/lib/useContent";
import MembershipWaitlist from "@/components/MembershipWaitlist";
import SEOHead from "@/components/SEOHead";
import SocialEcosystem from "@/components/SocialEcosystem";
import PhilosophyQuote from "@/components/PhilosophyQuote";
import PyramidPortal from "@/components/PyramidPortal";
import FeaturedEvents from "@/components/FeaturedEvents";
import CinematicImage from "@/components/CinematicImage";
import { resolveOrder, isVisible } from "@/lib/homepageSections";
import { PartnersTrustBand } from "@/components/PartnersStrip";
import StayConnected from "@/components/StayConnected";

const WORD_LINES = [
  ["ENTER"],
  ["A", "NEW"],
  ["DIMENSION."],
];

export default function Home() {
  const { get } = useContent("homepage");
  const [marqueeOpen, setMarqueeOpen] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem("tok_marquee_dismissed_v1") === "1") {
      setMarqueeOpen(false);
    }
  }, []);
  const dismissMarquee = () => {
    setMarqueeOpen(false);
    try {
      window.localStorage.setItem("tok_marquee_dismissed_v1", "1");
    } catch (e) {
      /* ignore */
    }
  };
  return (
    <div data-testid="home-page">
      <SEOHead
        title="Temple of Kulture · Immersive Cultural Ecosystem · Medway · London · Kent"
        description="An immersive cultural ecosystem fusing XR theatre, projection mapping, AI storytelling, Ubuntu wellness retreats and LEVEL UP youth programmes. Rooted in global majority stories. Medway, London, Kent."
        image={IMG.heroPortal}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Temple of Kulture",
          alternateName: "TOK",
          url: typeof window !== "undefined" ? window.location.origin : undefined,
          logo: TOK_ASSETS?.logo,
          description: "Immersive cultural ecosystem — XR theatre, projection mapping, AI storytelling, Ubuntu wellness retreats and LEVEL UP youth programmes.",
          areaServed: ["Medway", "London", "Kent", "United Kingdom"],
          sameAs: [
            "https://www.instagram.com/templeofkulture",
            "https://www.youtube.com/@templeofkulture",
          ],
        }}
      />
      {/* HERO — refined cinematic portal */}
      <section
        data-testid="hero-portal"
        className="relative min-h-[100svh] flex items-end overflow-hidden bg-black"
      >
        {/* Background plate — slow ken-burns drift with responsive focal */}
        <CinematicImage
          src={IMG.heroPortal || IMG.hero}
          mobile="55% 32%"
          desktop="center"
          scale={1.06}
          testId="home-hero-bg"
        />
        {/* Atmospheric stack — deeper, cinematic, layered */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_36%_56%,rgba(0,0,0,0)_0%,rgba(0,0,0,0.62)_55%,rgba(0,0,0,0.96)_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/88 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/88 via-black/40 to-transparent" />
        {/* Warm light shaft from upper-right */}
        <div className="tok-light-shaft" />
        {/* Soft amber bloom behind the headline */}
        <div className="tok-hero-bloom" />
        {/* Drifting dust motes */}
        <div className="tok-dust" />
        {/* Faint TOK monogram watermark — far-right, behind text */}
        <div className="hidden md:block absolute right-[-4%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.05]">
          <span className="font-display font-black uppercase tracking-tighter text-[28rem] leading-none text-amber-400 select-none">
            T·K
          </span>
        </div>
        {/* Grain layer */}
        <div className="absolute inset-0 tok-grain opacity-50 mix-blend-overlay pointer-events-none" />
        {/* Baseline atmospheric fog — fades hero into pure black */}
        <div className="tok-hero-fog" />

        {/* Floating show ticker — discreet, dismissible, desktop-only.
            Less visual weight than v1 (smaller text, lower opacity, no bg blur, close button). */}
        {marqueeOpen && (
          <div
            data-testid="hero-marquee"
            className="hidden md:flex absolute top-[72px] left-0 right-0 items-stretch border-b border-amber-500/8 bg-black/30 overflow-hidden z-10"
          >
            <div className="tok-marquee flex-1 whitespace-nowrap py-2 text-amber-400/70 font-display font-semibold uppercase tracking-[0.4em] text-[10px]">
              {Array.from({ length: 2 }).map((_, i) => (
                <span key={i} className="flex items-center">
                  {[
                    "The Outcasts",
                    "William Cuffay",
                    "Rebirth of a Mother",
                    "Buro Buro Mermaid",
                    "TOK Hip Hop Festival",
                    "Interactive Playground",
                    "XR Walking Theatre",
                    "xR Workshops",
                    "Playwright Lab",
                    "Virtual Production",
                    "Street & Kultural Dances",
                  ].map((t) => (
                    <span key={t + i} className="inline-flex items-center mx-10">
                      <span className="inline-block h-1 w-1 bg-amber-400/50 mr-5" />
                      {t}
                    </span>
                  ))}
                </span>
              ))}
            </div>
            <button
              type="button"
              data-testid="hero-marquee-dismiss"
              onClick={dismissMarquee}
              aria-label="Dismiss ticker"
              className="shrink-0 h-full px-3 flex items-center justify-center text-amber-400/50 hover:text-amber-400 hover:bg-black/50 transition-colors border-l border-amber-500/10"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        <Container className="relative z-10 pt-44 sm:pt-52 lg:pt-56 pb-28 sm:pb-36 w-full">
          {/* Kicker rail — vertical pulse + meta */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
            className="flex items-center gap-4 mb-12 sm:mb-14"
          >
            <span className="block h-12 w-px bg-gradient-to-b from-transparent via-amber-400 to-transparent tok-pulse-line" />
            <div>
              <p className="tok-eyebrow">
                {get("hero.eyebrow", TOK_VOICE.heroEyebrow)}
              </p>
              <p className="tok-meta mt-1">
                MEDWAY · LONDON · KENT
              </p>
            </div>
          </motion.div>

          {/* Headline — refined hierarchy & atmospheric depth */}
          <h1
            data-testid="hero-headline"
            className="tok-headline-hero max-w-[16ch] leading-[0.94] sm:leading-[0.92]"
            aria-label="Enter a new dimension."
            style={{ lineHeight: undefined }}
          >
            {WORD_LINES.map((line, li) => (
              <span
                key={li}
                className="block"
                style={{
                  // Subtle hierarchy: ENTER smaller prelude, A NEW focal, DIMENSION foundation
                  fontSize:
                    li === 0
                      ? "0.78em"
                      : li === 1
                      ? "1em"
                      : "1.06em",
                  letterSpacing: li === 0 ? "0.04em" : "-0.022em",
                  marginTop: li === 0 ? "0" : li === 1 ? "0.08em" : "0.04em",
                }}
              >
                {line.map((word, wi) => (
                  <motion.span
                    key={`${li}-${wi}`}
                    initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    transition={{
                      duration: 1.05,
                      delay: 0.4 + li * 0.22 + wi * 0.08,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className={`inline-block mr-[0.18em] ${
                      word === "NEW"
                        ? "tok-gold-rich"
                        : word === "DIMENSION."
                        ? "tok-mist-text"
                        : ""
                    }`}
                  >
                    {word}
                  </motion.span>
                ))}
              </span>
            ))}
          </h1>

          {/* Refined gold accent rule + sigil */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.5 }}
            className="flex items-center gap-4 mt-14 sm:mt-16"
          >
            <motion.span
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.2, delay: 1.5, ease: "easeOut" }}
              className="block origin-left h-px w-28 sm:w-40 bg-gradient-to-r from-amber-400 via-amber-300 to-transparent"
            />
            <span className="inline-block h-1.5 w-1.5 bg-amber-400 rotate-45" />
          </motion.div>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.7, ease: "easeOut" }}
            className="mt-9 sm:mt-10 text-[15px] sm:text-lg md:text-xl text-zinc-300 max-w-2xl leading-[1.7] font-light"
          >
            <span className="text-white/95">{get("hero.subtagline", TOK_VOICE.heroSubtagline)}</span>{" "}
            {get(
              "hero.subbody",
              "An immersive cultural ecosystem giving voice to the global majority through XR theatre, projection mapping, AI storytelling and live performance — rooted in untold stories."
            )}
          </motion.p>

          {/* CTA cluster */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.95, ease: "easeOut" }}
            className="mt-14 sm:mt-16 flex flex-wrap gap-3 sm:gap-4"
          >
            <Link
              to={get("hero.primaryCtaHref", "/join")}
              data-testid="hero-join-temple-btn"
              className="group inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-7 sm:px-9 py-4 sm:py-5 tok-cta-label transition-all hover:-translate-y-0.5"
            >
              <Sparkles className="h-4 w-4 group-hover:rotate-12 transition-transform" />
              {get("hero.primaryCtaLabel", "Join the Temple")}
            </Link>
            <Link
              to={get("hero.secondaryCtaHref", "/whats-on")}
              data-testid="hero-whats-on-btn"
              className="group inline-flex items-center gap-3 border border-amber-400/40 hover:border-amber-400 hover:bg-amber-400/5 text-amber-400 px-7 sm:px-9 py-4 sm:py-5 tok-cta-label transition-all"
            >
              <Calendar className="h-4 w-4" />
              {get("hero.secondaryCtaLabel", "See what's on")}
              <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          {/* Scroll cue */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 2.2 }}
            className="hidden sm:flex absolute right-8 lg:right-12 bottom-10 flex-col items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-zinc-500"
          >
            <span>Scroll</span>
            <span className="block h-10 w-px bg-gradient-to-b from-amber-400/60 to-transparent" />
            <ArrowDown className="h-3 w-3 text-amber-400 animate-bounce" />
          </motion.div>
        </Container>
      </section>

      {/* FEATURED EVENTS — premium homepage strip (right after hero). */}
      {isVisible(get("sections.visibility", {}), "featuredEvents") && <FeaturedEvents />}

      {/* MISSION — TOK's voice, verbatim */}
      <SectionDivider label="The Kulture" />
      <section className="relative py-20 sm:py-28 lg:py-40 border-y border-amber-500/10">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <FadeIn className="lg:col-span-5">
              <SectionLabel>Who we are</SectionLabel>
              <SectionHeading className="mt-6">
                Temple of <br />
                <span className="text-amber-400">Kulture.</span>
              </SectionHeading>
            </FadeIn>
            <FadeIn delay={0.15} className="lg:col-span-7 space-y-8">
              <p className="font-display font-black uppercase text-2xl sm:text-3xl leading-[1.2] text-white">
                {TOK_VOICE.missionHeading}
              </p>
              <p className="text-zinc-300 text-lg leading-relaxed">
                {TOK_VOICE.missionBody1}
              </p>
              <p className="text-zinc-300 text-lg leading-relaxed">
                {TOK_VOICE.missionBody2}
              </p>
              <p className="text-zinc-400 leading-relaxed italic">
                Discover all of our temple's offerings below.
              </p>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* PILLARS / BENTO */}
      <SectionDivider label="The Ecosystem" />
      <section className="relative py-20 sm:py-28 lg:py-36">
        <Container>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <SectionLabel>The ecosystem</SectionLabel>
              <SectionHeading className="mt-6">
                Six worlds. <br />
                <span className="text-amber-400">One temple.</span>
              </SectionHeading>
            </div>
            <p className="text-zinc-400 max-w-md">
              Explore the cultural pillars that shape every experience we
              create.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <FadeIn key={p.to} delay={i * 0.05}>
                <Link
                  to={p.to}
                  data-testid={`pillar-${p.label.toLowerCase().replace(/\s/g, "-")}-link`}
                  className="group relative block overflow-hidden border border-white/5 hover:border-amber-400/40 transition-all duration-500 aspect-[4/5]"
                >
                  <div
                    className="absolute inset-0 bg-cover transition-transform duration-[1500ms] group-hover:scale-110"
                    style={{
                      backgroundImage: `url(${p.image})`,
                      backgroundPosition: p.focal || "center",
                    }}
                  />
                  {/* Lighter gradient — lets the cinematic image breathe while
                      still anchoring the text in a readable dark band. */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/5" />
                  <div className="relative h-full p-8 flex flex-col justify-between">
                    <p className="tok-eyebrow-sm">
                      {p.label}
                    </p>
                    <div>
                      <h3 className="tok-headline-md leading-tight">
                        {p.title}
                      </h3>
                      <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
                        {p.desc}
                      </p>
                      <div className="mt-6 inline-flex items-center gap-2 tok-cta-label text-amber-400 group-hover:gap-4 transition-all">
                        Enter
                        <ArrowRight className="h-3.5 w-3.5" />
                      </div>
                    </div>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      {/* AUDIENCE PATHWAYS — under 16 / 16-25 / adults */}
      <SectionDivider label="For Every Age" />
      <section
        data-testid="audience-pathways"
        className="relative py-20 sm:py-28 lg:py-32 bg-black"
      >
        <Container>
          <div className="max-w-3xl mb-14">
            <p className="tok-eyebrow">Audience pathways</p>
            <h2 className="tok-headline-xl mt-3">
              Where you fit <br />
              in the <span className="text-amber-400">Temple.</span>
            </h2>
            <p className="mt-5 text-zinc-300 leading-relaxed max-w-2xl">
              We're an ecosystem, not a single venue. Find the door that opens
              for you — every pathway leads somewhere alive.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-4">
            <PathwayCard
              label="Under 16"
              title="Interactive Playground & Home Education"
              body="Movement-led projection games, weekly Wed/Thu Home Education for global majority families, after-school clubs, schools workshops and family immersive experiences."
              links={[
                { to: "/home-education", label: "Home Education", testid: "pathway-home-education" },
                { to: "/experiences", label: "Interactive Playground", testid: "pathway-playground" },
                { to: "/community", label: "Family experiences", testid: "pathway-family" },
              ]}
            />
            <PathwayCard
              label="16 – 25"
              title="LEVEL UP"
              body="The annual youth development pathway: XR, immersive arts, AI creation, filmmaking, performance, music, employability, wellbeing — with progression routes into University of Kent, Rose Bruford, apprenticeships, FE and paid creative work."
              accent
              links={[
                { to: "/level-up-xr", label: "Discover LEVEL UP", testid: "pathway-level-up" },
                { to: "/level-up-xr#apply", label: "Apply / Register interest", testid: "pathway-level-up-apply" },
              ]}
            />
            <PathwayCard
              label="Adults"
              title="Ubuntu Retreats, Kulture Lab & social rituals"
              body="Monthly Ubuntu Immersive Retreats (wellness), public immersive shows, Afro Anime socials, Immersive Brunch, exhibitions, corporate experiences and Kulture Lab residencies."
              links={[
                { to: "/ubuntu-retreat", label: "Ubuntu Retreat", testid: "pathway-ubuntu" },
                { to: "/experiences", label: "Immersive experiences", testid: "pathway-experiences" },
                { to: "/xr-studio", label: "Kulture Lab / XR", testid: "pathway-xr" },
                { to: "/corporate", label: "Corporate", testid: "pathway-corporate" },
              ]}
            />
          </div>
        </Container>
      </section>

      {/* STAY CONNECTED — primary conversion block (newsletter + WhatsApp). */}
      <SectionDivider label="Stay In The Temple" />
      <div className="bg-black border-y border-amber-500/10">
        <StayConnected pageSource="home" testid="home-stay-connected" />
      </div>

      {/* FEATURED — Windrush Immersive Experience */}
      <SectionDivider label="Featured Work" />
      <section
        data-testid="windrush-featured-section"
        className="relative py-16 sm:py-24 lg:py-28 overflow-hidden"
      >
        <Container>
          <FadeIn>
            <Link
              to="/shows/windrush-immersive-experience"
              data-testid="windrush-featured-link"
              className="group relative block overflow-hidden border border-amber-400/30 hover:border-amber-400 transition-colors"
            >
              <div className="relative aspect-[16/9] sm:aspect-[16/9] overflow-hidden bg-zinc-900">
                <img
                  src={TOK_ASSETS.windrushBanner}
                  alt="The Windrush Immersive Experience — memory, movement and legacy"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                  style={{ objectPosition: "center" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              </div>
              <div className="bg-zinc-950 p-6 sm:p-10 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold flex items-center gap-3">
                    <span className="inline-block h-2 w-2 bg-amber-400 tok-pulse-gold" />
                    Featured immersive experience
                  </p>
                  <h3 className="mt-4 tok-headline-xl">
                    The Windrush <br />
                    <span className="text-amber-400">Immersive Experience.</span>
                  </h3>
                  <p className="mt-4 text-zinc-300 max-w-xl leading-relaxed">
                    A journey through memory, movement and legacy. 22 – 26 June at HYPHA Studio, Chatham. Pre-booking required.
                  </p>
                </div>
                <span className="inline-flex items-center gap-3 bg-amber-400 group-hover:bg-amber-300 text-black px-7 py-4 font-bold uppercase tracking-[0.25em] text-xs transition-colors shrink-0">
                  Step inside <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          </FadeIn>
        </Container>
      </section>

      {/* IMMERSIVE SHOWS — TOK's signature productions */}
      <SectionDivider label="Signature Productions" />
      <section
        data-testid="immersive-shows-section"
        className="relative py-28 sm:py-36 bg-black"
      >
        <Container>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <FadeIn>
              <SectionLabel>Immersive shows</SectionLabel>
              <SectionHeading className="mt-6">
                Untold stories, <br />
                <span className="text-amber-400">told whole.</span>
              </SectionHeading>
            </FadeIn>
            <p className="text-zinc-400 max-w-md">
              The signature TOK productions — immersive theatre using XR
              technology to put audiences inside the story.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TOK_SHOWS.filter((s) => s.id !== "windrush-immersive-experience").map((s, i) => {
              const inner = (
                <>
                  {/* Image zone — fixed 4:3 so all four cards align identically.
                      Per-show focal point keeps the subject visible. */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-zinc-900">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] group-hover:scale-105"
                      style={{ objectPosition: s.focal || "center" }}
                    />
                    {/* Subtle floor gradient — readability shadow without
                        covering the focal subject. */}
                    <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/85 via-black/30 to-transparent pointer-events-none" />
                  </div>
                  {/* Text zone — solid dark strip, fixed min-height so titles
                      and descriptions of different lengths still align. */}
                  <div className="flex-1 flex flex-col justify-between p-6 bg-zinc-950">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                        {s.eyebrow}
                      </p>
                      <h3 className="mt-3 font-display font-bold uppercase text-2xl leading-tight">
                        {s.title}
                      </h3>
                      <p className="mt-3 text-xs text-zinc-400 leading-relaxed line-clamp-3">
                        {s.desc}
                      </p>
                    </div>
                    <div className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold group-hover:gap-3 transition-all">
                      Step inside
                      <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>
                </>
              );
              const className =
                "group flex flex-col overflow-hidden border border-white/5 hover:border-amber-400/50 transition-colors min-h-[480px]";
              return (
                <FadeIn key={s.id} delay={i * 0.06}>
                  {s.external ? (
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`tok-show-${s.id}`}
                      className={className}
                    >
                      {inner}
                    </a>
                  ) : (
                    <Link
                      to={s.href}
                      data-testid={`tok-show-${s.id}`}
                      className={className}
                    >
                      {inner}
                    </Link>
                  )}
                </FadeIn>
              );
            })}
          </div>
        </Container>
      </section>

      {/* TEMPLE ACTIVITIES — verbatim from TOK site */}
      <section
        data-testid="temple-activities-section"
        className="relative py-28 sm:py-36 overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${TOK_ASSETS.sqPlaywriting})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
        <Container className="relative">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <FadeIn className="lg:col-span-5">
              <SectionLabel>Temple Activities</SectionLabel>
              <SectionHeading className="mt-6">
                Workshops, <br />
                <span className="text-amber-400">labs</span>, programmes.
              </SectionHeading>
              <p className="mt-8 text-zinc-300 text-lg leading-relaxed max-w-md">
                At Temple of Kulture, we're striving to expand our immersive
                experiences to meet the needs of our community.
              </p>
            </FadeIn>
            <FadeIn delay={0.15} className="lg:col-span-7">
              <ul className="grid sm:grid-cols-2 gap-3">
                {TOK_VOICE.templeActivities.map((a, i) => (
                  <li
                    key={a}
                    data-testid={`temple-activity-${i}`}
                    className="flex items-center gap-3 border border-white/5 bg-zinc-950/80 backdrop-blur-md px-5 py-4 hover:border-amber-400/40 transition-colors"
                  >
                    <span className="text-amber-400 font-display font-bold text-sm w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display font-semibold text-sm sm:text-base">
                      {a}
                    </span>
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* IMPACT BAR */}
      <section className="relative py-20 bg-black border-y border-amber-500/10">
        <Container>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
            {IMPACT_STATS.map((s, i) => (
              <FadeIn key={s.label} delay={i * 0.08}>
                <div data-testid={`stat-${i}`}>
                  <p className="font-display font-black text-5xl sm:text-6xl text-amber-400 leading-none">
                    {s.value}
                  </p>
                  <p className="mt-3 text-xs uppercase tracking-[0.25em] text-zinc-400">
                    {s.label}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      {/* COMMUNITY SPOTLIGHT — real TOK photograph */}
      <section
        data-testid="community-spotlight"
        className="relative py-28 sm:py-36 overflow-hidden"
      >
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <FadeIn className="lg:col-span-6">
              <div className="relative aspect-[4/5] overflow-hidden border border-amber-400/20">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${TOK_ASSETS.communityVR})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                    From inside the temple
                  </p>
                  <p className="mt-2 text-white font-display text-lg">
                    Kulture Lab — XR pilot session
                  </p>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.15} className="lg:col-span-6">
              <SectionLabel>Built with our community</SectionLabel>
              <SectionHeading className="mt-6">
                Real people. <br />
                <span className="text-amber-400">Real worlds.</span>
              </SectionHeading>
              <p className="mt-8 text-zinc-300 text-lg leading-relaxed max-w-md">
                Every experience we make is shaped with — and for — the
                community we come from. Global majority artists, technologists
                and storytellers at the centre of every decision.
              </p>
              <p className="mt-4 text-zinc-400 leading-relaxed max-w-md">
                Step into Kulture Lab and you'll find members of our world
                testing XR theatre pieces, prototyping projection sets and
                shaping the next season of TOK alongside us.
              </p>
              <Link
                to="/community"
                data-testid="spotlight-community-link"
                className="mt-10 inline-flex items-center gap-3 text-amber-400 font-bold uppercase tracking-[0.25em] text-xs hover:gap-5 transition-all"
              >
                Meet our community <ArrowRight className="h-4 w-4" />
              </Link>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* WHAT WE DO ICON GRID */}
      <section className="py-28 sm:py-36">
        <Container>
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <FadeIn>
              <SectionLabel>What we build</SectionLabel>
              <SectionHeading className="mt-6">
                Culture, technology, <br />
                <span className="text-amber-400">community.</span>
              </SectionHeading>
              <p className="mt-8 text-zinc-300 leading-relaxed text-lg max-w-md">
                Our practice spans immersive nights, education, corporate
                commissions, festivals and XR R&D. Every project sits at the
                meeting point of heritage and future.
              </p>
              <Link
                to="/impact"
                className="mt-10 inline-flex items-center gap-3 text-amber-400 font-bold uppercase tracking-[0.25em] text-xs hover:gap-5 transition-all"
                data-testid="home-impact-link"
              >
                Our impact <ArrowRight className="h-4 w-4" />
              </Link>
            </FadeIn>
            <div className="grid grid-cols-2 gap-4">
              {[
                { Icon: Sparkles, t: "Immersive Experiences", to: "/experiences" },
                { Icon: Cpu, t: "XR & AI Storytelling", to: "/xr-studio" },
                { Icon: Music, t: "Nightlife & Sound", to: "/experiences" },
                { Icon: Palette, t: "Creative Education", to: "/level-up-xr" },
                { Icon: Users, t: "Community Programmes", to: "/community" },
                { Icon: Globe2, t: "Global Majority Stories", to: "/impact" },
              ].map(({ Icon, t, to }, i) => (
                <FadeIn key={t} delay={i * 0.05}>
                  <Link
                    to={to}
                    data-testid={`home-pillar-card-${t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                    className="group relative block border border-white/5 bg-zinc-950 p-6 hover:border-amber-400/60 hover:bg-amber-400/[0.04] transition-all h-full"
                  >
                    <Icon className="h-7 w-7 text-amber-400" />
                    <p className="mt-6 font-display font-semibold text-base sm:text-lg group-hover:text-amber-400 transition-colors">
                      {t}
                    </p>
                    <ArrowRight className="absolute top-6 right-6 h-4 w-4 text-zinc-600 group-hover:text-amber-400 group-hover:translate-x-1 transition-all" />
                  </Link>
                </FadeIn>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* FEATURED CTA */}
      <section className="relative py-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${TOK_ASSETS.sqEnterDimension})` }}
        />
        <div className="absolute inset-0 bg-black/75" />
        <Container className="relative">
          <FadeIn className="max-w-3xl">
            <SectionLabel>Flagship 2026</SectionLabel>
            <SectionHeading className="mt-6">
              TOK Hip Hop Festival <br />
              & <span className="text-amber-400">TOK XR Festival</span>
            </SectionHeading>
            <p className="mt-8 text-zinc-300 text-lg leading-relaxed">
              Two flagship festivals in 2026. One celebrates the raw, current
              pulse of UK & diasporic hip hop. The other reveals what culture
              looks like when projection, AI and live performance fuse into a
              single world.
            </p>
            <Link
              to="/festivals"
              data-testid="festivals-cta"
              className="mt-10 inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-8 py-5 font-bold uppercase tracking-[0.25em] text-xs transition-colors"
            >
              Explore festivals <ArrowRight className="h-4 w-4" />
            </Link>
          </FadeIn>
        </Container>
      </section>

      {/* MEMBERSHIP WAITLIST */}
      <MembershipWaitlist />

      {/* PARTNERS TRUST BAND — real funders, councils, venues */}
      <PartnersTrustBand />

      {/* MANAGED CINEMATIC BLOCK — visibility + drag-and-drop reorder via CMS */}
      <ManagedSections
        order={resolveOrder(get("sections.order", null))}
        visibility={get("sections.visibility", {})}
        get={get}
      />

      {/* TESTIMONIALS */}
      <section className="py-28 sm:py-36">
        <Container>
          <FadeIn>
            <SectionLabel>Voices</SectionLabel>
            <SectionHeading className="mt-6 max-w-3xl">
              People don't <br />
              <span className="text-amber-400">leave</span> the same.
            </SectionHeading>
          </FadeIn>
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <FadeIn key={t.name} delay={i * 0.08}>
                <blockquote className="border border-white/5 bg-zinc-950 p-8 h-full flex flex-col justify-between hover:border-amber-400/30 transition-colors">
                  <p className="text-zinc-200 text-lg leading-relaxed font-display">
                    “{t.quote}”
                  </p>
                  <footer className="mt-8 pt-6 border-t border-white/5">
                    <p className="text-amber-400 font-bold text-sm">{t.name}</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-[0.18em] mt-1">
                      {t.role}
                    </p>
                  </footer>
                </blockquote>
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      {/* PRESS MARQUEE */}
      <section className="py-20 border-y border-amber-500/10 bg-black overflow-hidden">
        <Container className="mb-10">
          <SectionLabel>As featured in</SectionLabel>
        </Container>
        <div className="overflow-hidden">
          <div className="tok-marquee whitespace-nowrap">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className="inline-flex">
                {PRESS.map((p) => (
                  <span
                    key={p + i}
                    className="mx-10 font-display font-bold uppercase tracking-[0.25em] text-2xl sm:text-3xl text-zinc-500"
                  >
                    {p}
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATIONS — editorial composition, baseline-aligned, intentional spacing */}
      <section
        data-testid="locations-section"
        className="relative py-28 sm:py-36 bg-black border-b border-amber-500/10 overflow-hidden"
      >
        {/* Faint amber rule running through the baseline of the cities */}
        <div className="absolute left-0 right-0 top-1/2 mt-6 sm:mt-10 h-px bg-amber-400/10 pointer-events-none" />

        <Container className="relative">
          <div className="grid lg:grid-cols-12 gap-y-12 lg:gap-x-16 items-end">
            <FadeIn className="lg:col-span-3">
              <p className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold inline-flex items-center gap-3">
                <MapPin className="h-3 w-3" />
                Where we work
              </p>
              <p className="mt-5 text-sm text-zinc-400 leading-relaxed max-w-xs">
                Three regions, one ecosystem. Studios, venues and partner
                networks across the south-east.
              </p>
            </FadeIn>

            <FadeIn delay={0.15} className="lg:col-span-9">
              <ul className="flex flex-wrap items-baseline gap-x-10 sm:gap-x-16 lg:gap-x-20 gap-y-6">
                {TOK_VOICE.locations.map((city, i) => (
                  <li
                    key={city}
                    data-testid={`location-${city.toLowerCase()}`}
                    className="font-display font-black uppercase tracking-tighter leading-none text-white text-[4.5rem] sm:text-[6.5rem] lg:text-[8rem] xl:text-[9rem]"
                    style={{ animationDelay: `${i * 80}ms` }}
                  >
                    <span className="align-baseline">{city}</span>
                    <span className="ml-2 align-baseline text-amber-400">.</span>
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* DONATION */}
      <section
        data-testid="donation-section"
        className="relative py-28 sm:py-36 overflow-hidden"
      >        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${TOK_ASSETS.whoWeAre})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
        <Container className="relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <FadeIn className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-2 border border-amber-400/40 text-amber-400 text-[10px] uppercase tracking-[0.35em] font-bold mb-8">
                <Heart className="h-3.5 w-3.5" />
                Support the Temple
              </div>
              <h2 className="tok-headline-xl">
                {TOK_VOICE.donationHeading}
              </h2>
              <p className="mt-8 text-zinc-300 text-lg leading-relaxed max-w-2xl">
                {TOK_VOICE.donationBody}
              </p>
            </FadeIn>
            <FadeIn delay={0.15} className="lg:col-span-5">
              <a
                href="https://www.templeofkulture.co.uk/contact"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="donation-cta"
                className="block bg-amber-400 hover:bg-amber-300 text-black p-10 transition-colors"
              >
                <p className="text-[10px] uppercase tracking-[0.3em] font-bold">
                  Make a contribution
                </p>
                <p className="mt-4 font-display font-black text-4xl sm:text-5xl leading-none">
                  Donate
                </p>
                <p className="mt-6 text-sm leading-relaxed">
                  Empower the unheard. Every contribution helps us tell more
                  untold stories.
                </p>
                <ArrowRight className="h-6 w-6 mt-8" />
              </a>
            </FadeIn>
          </div>
        </Container>
      </section>

      {/* CORE PHILOSOPHY now rendered inside ManagedSections above. */}
    </div>
  );
}

function ManagedSections({ order, visibility, get }) {
  const renderers = {
    midPhilosophy: () =>
      isVisible(visibility, "midPhilosophy") && (
        <PhilosophyQuote
          key="midPhilosophy"
          variant="banner"
          lineKey={get("midPhilosophyLineKey", "rhythm-image-story")}
          testId="home-mid-philosophy"
        />
      ),
    socialEcosystem: () =>
      isVisible(visibility, "socialEcosystem") && (
        <SocialEcosystem
          key="socialEcosystem"
          eyebrow={get("social.eyebrow", "Follow the ecosystem")}
          title={get("social.title", "Enter the ecosystem beyond the stage.")}
          copy={get(
            "social.copy",
            "Follow TOK across Instagram, TikTok, YouTube and Twitch."
          )}
        />
      ),
    pyramidPortal: () =>
      isVisible(visibility, "pyramidPortal") && (
        <PyramidPortal
          key="pyramidPortal"
          image={get("pyramid.image", IMG.hero)}
          eyebrow={get("pyramid.eyebrow", "The temple speaks")}
          line={get("philosophy.coreLine", TOK_PHILOSOPHY.core.line)}
          attribution={get(
            "philosophy.coreAttribution",
            TOK_PHILOSOPHY.core.attribution
          )}
          height="tall"
          testId="home-core-philosophy"
        />
      ),
    // Future-ready slot — renders nothing on the live site until the team
    // turns it on AND supplies testimonials/funder quotes via the CMS.
    testimonials: () => null,
  };
  return <>{order.map((k) => (renderers[k] ? renderers[k]() : null))}</>;
}

function PathwayCard({ label, title, body, links, accent = false }) {
  return (
    <FadeIn>
      <article
        data-testid={`pathway-card-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className={`group relative h-full p-7 sm:p-8 border transition-colors ${
          accent
            ? "border-amber-400/50 bg-gradient-to-br from-amber-400/10 via-transparent to-transparent hover:border-amber-400"
            : "border-white/10 bg-zinc-950 hover:border-amber-400/40"
        }`}
      >
        <p
          className={`tok-eyebrow-sm ${
            accent ? "text-amber-300" : ""
          }`}
        >
          {label}
        </p>
        <h3 className="tok-headline-md mt-3 leading-tight">{title}</h3>
        <p className="mt-4 text-sm text-zinc-300 leading-relaxed">{body}</p>
        <ul className="mt-7 space-y-2.5">
          {links.map((l) => (
            <li key={l.to + l.label}>
              <Link
                to={l.to}
                data-testid={l.testid}
                className="group/link inline-flex items-center gap-2 tok-cta-label text-amber-400 hover:text-amber-300 transition-colors"
              >
                {l.label}
                <ArrowRight className="h-3 w-3 group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </li>
          ))}
        </ul>
      </article>
    </FadeIn>
  );
}

